import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Breadcrumb, Button, Spin, Tag, message } from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  TagOutlined,
  UserOutlined,
  PaperClipOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import axios from '../../../config/axiosInstance';
import API_BASE_URL from '../../../config/api';
import getFileUrl from '../../../utils/fileUrl';
import { Helmet } from 'react-helmet-async';
import './news.css';

const MONTHS_TH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

// แปลงวันที่ ISO -> ไทย (พ.ศ.)
const formatThaiDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS_TH[d.getMonth()]} ${d.getFullYear() + 543}`;
};

// แปลงขนาดไฟล์เป็น KB/MB
const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const NewsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/news/${id}`);
        if (!cancelled) setNews(res.data);
      } catch {
        if (!cancelled) message.error('โหลดข่าวล้มเหลว');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // ดึงไฟล์เอกสารแนบของข่าว
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/news-attachments/${id}`)
      .then((res) => {
        if (!cancelled) setAttachments(res.data?.attachments || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    document.title = news?.title
      ? `${news.title} | สำนักงานเจ้าคณะภาค 4-5 (ธรรมยุต)`
      : 'ข่าวสาร | สำนักงานเจ้าคณะภาค 4-5 (ธรรมยุต)';
  }, [news]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin tip="กำลังโหลด..." size="large" />
      </div>
    );
  }

  if (!news) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0', color: '#8a6d3b' }}>
        ไม่พบข่าวที่ต้องการ
      </div>
    );
  }

  return (
    <div className="news-detail-page">
      <Helmet>
        <title>{news.title} | สำนักงานเจ้าคณะภาค 4-5 (ธรรมยุต)</title>
        <meta name="description" content="ระบบสารสนเทศคณะสงฆ์ ภาค 4-5 (ธรรมยุต)" />
        <meta property="og:title" content={`${news.title} | สำนักงานเจ้าคณะภาค 4-5 (ธรรมยุต)`} />
        <meta property="og:description" content="ระบบสารสนเทศคณะสงฆ์ ภาค 4-5 (ธรรมยุต)" />
        <meta property="og:image" content={news.cover_image} />
      </Helmet>

      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        className="news-back-btn"
        onClick={() => navigate(-1)}
      >
        ย้อนกลับ
      </Button>

      <Breadcrumb
        className="news-detail-breadcrumb"
        items={[
          { title: <Link to="/">หน้าหลัก</Link> },
          { title: <Link to="/news">ข่าวสาร</Link> },
          { title: news.title },
        ]}
      />

      <article>
        {/* ส่วนหัวข่าว (ไม่แสดงภาพ Cover — ซ้ำกับเนื้อหา) */}
        <div className="news-detail-head">
          <div className="news-detail-meta">
            {news.category_name && (
              <Tag color="gold" icon={<TagOutlined />}>
                {news.category_name}
              </Tag>
            )}
            {news.published_at && (
              <span>
                <CalendarOutlined /> {formatThaiDate(news.published_at)}
              </span>
            )}
          </div>
          <h1 className="news-detail-title">{news.title}</h1>
        </div>

        <div className="news-detail-card">
          <div
            className="news-detail-content ql-editor"
            dangerouslySetInnerHTML={{ __html: news.content }}
          />

          {attachments.length > 0 && (
            <div className="news-attachments">
              <h3 className="news-attachments-title">
                <PaperClipOutlined /> เอกสารแนบ ({attachments.length})
              </h3>
              {attachments.map((att) => (
                <div className="news-attachments-item" key={att.id}>
                  <PaperClipOutlined className="news-attachments-icon" />
                  <span className="news-attachments-name">{att.original_name}</span>
                  <span className="news-attachments-size">
                    ({formatFileSize(att.file_size)})
                  </span>
                  <Button
                    type="primary"
                    size="small"
                    className="news-attachments-dl"
                    icon={<DownloadOutlined />}
                    href={getFileUrl(att.ref, 'news-attachments')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ดาวน์โหลด
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="news-detail-footer">
            {news.author ? (
              <span className="news-detail-footer-author">
                <UserOutlined /> โดย {news.author}
              </span>
            ) : (
              <span />
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default NewsDetailPage;

